/**
 * Vista "Linha do tempo" — histórico completo (auditoria) da Gestão de
 * Solicitações: quem abriu, autorizou, recusou, moveu no quadro, classificou,
 * replanejou prazo, separou, marcou pronta, registrou retirada, gerou lote…
 * Agrupa por dia (mais recente primeiro) com chips por tipo de evento. Mesmo
 * desenho da Linha do tempo de Requisições de Compra (LinhaEventos).
 *
 * Os filtros da tela valem aqui: tipo/situação/cliente/solicitante/busca
 * filtram a SOLICITAÇÃO do evento; o período filtra a DATA DO EVENTO (uma
 * solicitação de agosto autorizada hoje aparece em hoje).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheckSquare, FiClock, FiColumns, FiFileText, FiImage, FiLayers, FiPackage,
  FiPlus, FiRefreshCw, FiTruck,
} from "react-icons/fi";
import styled from "styled-components";

import {
  SITUACAO_LABELS,
  SITUACAO_TONES,
  listarEventosSolicitacoes,
} from "../../../services/solicitacoes";
import { getToneTokens } from "../../../styles/shared";
import { Chip, Mono, Pill, Tag } from "../_shared/quadro/styles";
import { Carregando } from "../_shared/Estados";
import { chaveSolicitacao } from "./helpers";

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-1);
  overflow: hidden;
`;

const Cabeca = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);
  > .info { margin-left: auto; font-size: 12px; color: var(--ink-4); white-space: nowrap; }
`;

const Dia = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  border-bottom: 1px solid var(--border-subtle);
  &:last-child { border-bottom: 0; }
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const DiaCol = styled.div`
  padding: 14px 16px;
  border-right: 1px solid var(--border-subtle);
  background: ${({ $hoje }) => ($hoje ? "var(--accent-soft)" : "transparent")};
  > .dow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: ${({ $hoje }) => ($hoje ? "var(--accent)" : "var(--ink-3)")}; }
  > .dia { font-size: 22px; font-weight: 700; line-height: 1.2; }
  > .mes { font-size: 12px; color: var(--ink-3); }
  @media (max-width: 720px) { border-right: 0; border-bottom: 1px solid var(--border-subtle); }
`;

const Evento = styled.button`
  display: grid;
  grid-template-columns: 52px 28px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 9px 16px;
  border: none;
  border-bottom: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--ink);
  text-align: left;
  font: inherit;
  cursor: ${({ $abre }) => ($abre ? "pointer" : "default")};
  &:last-child { border-bottom: 0; }
  &:hover { background: ${({ $abre }) => ($abre ? "var(--surface-hover)" : "transparent")}; }
  > .hora { font-family: var(--font-mono); font-size: 11.5px; color: var(--ink-4); }
  > .texto { min-width: 0; }
  > .texto .l1 { font-size: 13px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  > .texto .l2 { font-size: 12px; color: var(--ink-2); display: flex; align-items: center; gap: 6px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  @media (max-width: 560px) {
    grid-template-columns: 44px 28px minmax(0, 1fr);
    > .tag { display: none; }
  }
`;

const Icone = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $tone }) => { const t = getToneTokens(null, $tone); return `background: ${t.background}; color: ${t.text};`; }}
`;

const Aviso = styled.div`
  padding: 32px;
  text-align: center;
  color: var(--ink-3);
  font-size: 13px;
`;

const ICONE_POR_TIPO = {
  nova: FiPlus, status: FiRefreshCw, quadro: FiColumns, classificacao: FiCheckSquare,
  prazo: FiClock, separacao: FiPackage, expedicao: FiTruck, lote: FiLayers, foto: FiImage,
};

function chaveDia(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function hora(iso) {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function PillMini({ situacao }) {
  return (
    <Pill $tone={SITUACAO_TONES[situacao] || "neutral"} style={{ height: 20, fontSize: 11, padding: "0 8px" }}>
      {SITUACAO_LABELS[situacao] || situacao}
    </Pill>
  );
}

/** Evento sem solicitação viva (lote, excluída) não tem o que filtrar nem abrir. */
export function eventoPassaNosFiltros(evento, chavesVisiveis) {
  if (!evento.solicitacao_id) return true;
  return chavesVisiveis.has(chaveSolicitacao({ familia: evento.familia, id: evento.solicitacao_id }));
}

export default function LinhaTempo({ request, itens, periodo, onAbrir, versao }) {
  const [eventos, setEventos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [tipo, setTipo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await listarEventosSolicitacoes(request, {
        desde: periodo?.de || undefined, ate: periodo?.ate || undefined, limite: 1000,
      });
      setEventos(r?.eventos || []);
      setTipos(r?.tipos || []);
      setErro("");
    } catch (e) {
      setErro(e?.message || "Não foi possível carregar a linha do tempo.");
    } finally {
      setCarregando(false);
    }
  }, [request, periodo?.de, periodo?.ate]);

  // `versao` muda a cada recarga da lista (ação feita na tela) → refaz o feed.
  useEffect(() => { carregar(); }, [carregar, versao]);

  const chavesVisiveis = useMemo(() => new Set((itens || []).map(chaveSolicitacao)), [itens]);
  const visiveis = useMemo(
    () => eventos.filter((e) => eventoPassaNosFiltros(e, chavesVisiveis)),
    [eventos, chavesVisiveis],
  );

  const contagem = useMemo(() => {
    const c = {};
    visiveis.forEach((e) => { c[e.tipo] = (c[e.tipo] || 0) + 1; });
    return c;
  }, [visiveis]);

  const grupos = useMemo(() => {
    const hojeK = chaveDia(new Date());
    const map = new Map();
    visiveis.filter((e) => !tipo || e.tipo === tipo).forEach((e) => {
      const d = new Date(e.timestamp);
      const k = chaveDia(d);
      if (!map.has(k)) map.set(k, { data: d, hoje: k === hojeK, itens: [] });
      map.get(k).itens.push(e);
    });
    return Array.from(map.values()).sort((a, b) => b.data - a.data);
  }, [visiveis, tipo]);

  if (carregando && eventos.length === 0) return <Carregando />;

  return (
    <Card>
      <Cabeca>
        <Chip type="button" $on={!tipo} onClick={() => setTipo(null)}>
          Todos <span className="n">{visiveis.length}</span>
        </Chip>
        {tipos.filter((t) => contagem[t.value]).map((t) => (
          <Chip key={t.value} type="button" $on={tipo === t.value} $cor={getToneTokens(null, t.tone).accent} onClick={() => setTipo(tipo === t.value ? null : t.value)}>
            <span className="dot" /> {t.label} <span className="n">{contagem[t.value]}</span>
          </Chip>
        ))}
        <span className="info">
          Histórico completo · {visiveis.length} eventos{carregando ? " · atualizando…" : ""}
        </span>
      </Cabeca>

      {erro ? <Aviso>{erro}</Aviso> : null}
      {!erro && grupos.length === 0 ? (
        <Aviso>Nenhum evento no período com os filtros atuais.</Aviso>
      ) : grupos.map((g) => (
        <Dia key={g.data.toISOString()}>
          <DiaCol $hoje={g.hoje}>
            <div className="dow">{g.hoje ? "Hoje" : g.data.toLocaleDateString("pt-BR", { weekday: "long" })}</div>
            <div className="dia">{g.data.getDate()}</div>
            <div className="mes">{g.data.toLocaleDateString("pt-BR", { month: "long" })} · {g.itens.length} eventos</div>
          </DiaCol>
          <div style={{ minWidth: 0 }}>
            {g.itens.map((e) => {
              const Ico = ICONE_POR_TIPO[e.tipo] || FiFileText;
              const meta = tipos.find((t) => t.value === e.tipo);
              const abre = Boolean(e.solicitacao_id);
              return (
                <Evento
                  key={e.id} type="button" $abre={abre}
                  title={abre ? "Abrir solicitação" : undefined}
                  onClick={() => abre && onAbrir({ familia: e.familia, id: e.solicitacao_id, numero: e.numero })}
                >
                  <span className="hora">{hora(e.timestamp)}</span>
                  <Icone $tone={meta?.tone || "neutral"}><Ico size={14} /></Icone>
                  <div className="texto">
                    <div className="l1">
                      <strong>{e.ator_nome || "Sistema"}</strong>
                      <span style={{ color: "var(--ink-2)" }}>{e.detalhe}</span>
                      {e.para ? (
                        <>
                          {e.de ? <><PillMini situacao={e.de} /><span style={{ color: "var(--ink-4)" }}>→</span></> : null}
                          <PillMini situacao={e.para} />
                        </>
                      ) : null}
                    </div>
                    <div className="l2">
                      {e.numero ? <Mono $size={11} style={{ color: "var(--accent)" }}>{e.numero}</Mono> : null}
                      {e.cliente_nome ? <span>· {e.cliente_nome}</span> : null}
                      {(e.solicitacoes || []).length ? <span>· {e.solicitacoes.join(", ")}</span> : null}
                    </div>
                  </div>
                  <Tag className="tag" $tone="neutral" $h={22} style={{ background: "var(--surface-2)", color: "var(--ink-3)" }}>
                    {meta?.label || e.tipo}
                  </Tag>
                </Evento>
              );
            })}
          </div>
        </Dia>
      ))}
    </Card>
  );
}
